// Copyright (c) Ping. All rights reserved.
//
// "Has this device completed a full session bootstrap before?" marker.
//
// Drives the one-time bootstrapping loader: the full-screen
// `<BootstrappingScreen/>` should only appear on a genuine first
// signup/login on this device. On every subsequent reload we render
// the cached AppShell immediately (the chat list hydrates from
// IndexedDB via the conversations adapter, independent of the SDK)
// and re-initialise the SDK silently in the background.
//
// Two-layer storage:
//   - localStorage: a SYNCHRONOUS hint read in App's `useState`
//     initializer so the very first paint can pick the right phase
//     with no loader flash. localStorage is the no-flash hint only.
//   - IndexedDB `settings_kv`: the durable source of truth, written
//     alongside the localStorage hint so the two stay in sync.
//
// Cleared automatically by `clearAllLocalSessionState` on logout /
// arrival wipes (it clears `settings_kv` + aggressively wipes
// localStorage), so a hard account switch naturally re-shows the
// loader on the next first login.

import { db } from '@core/persistence/db';

const BOOTSTRAP_ONCE_KV_KEY = 'session.hasBootstrappedOnce';
const BOOTSTRAP_ONCE_LS_KEY = 'ping.session.hasBootstrappedOnce';

/**
 * Synchronous no-flash hint. Safe to call in a `useState` initializer.
 * Returns `true` once this device has completed at least one full
 * bootstrap. Falls back to `false` if localStorage is unavailable
 * (private mode edge) — worst case the loader shows once more.
 */
export function readHasBootstrappedOnceSync(): boolean {
  try {
    return (
      typeof localStorage !== 'undefined' &&
      localStorage.getItem(BOOTSTRAP_ONCE_LS_KEY) === '1'
    );
  } catch {
    return false;
  }
}

/**
 * Durable read (IndexedDB). Used as a belt-and-suspenders confirm; the
 * localStorage hint is what gates the first paint.
 */
export async function readHasBootstrappedOnce(): Promise<boolean> {
  try {
    await db.open();
    const row = await db.settings_kv.get(BOOTSTRAP_ONCE_KV_KEY);
    return Boolean(row?.value);
  } catch {
    return readHasBootstrappedOnceSync();
  }
}

/**
 * Stamp the device as having completed a full bootstrap. Idempotent.
 * Call only AFTER the session store reaches `authenticated` (i.e. the
 * SDK is live), so a failed first attempt re-shows the loader.
 */
export async function markBootstrappedOnce(): Promise<void> {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(BOOTSTRAP_ONCE_LS_KEY, '1');
    }
  } catch {
    /* private mode — IDB write below still records it durably */
  }
  try {
    await db.open();
    await db.settings_kv.put({
      key: BOOTSTRAP_ONCE_KV_KEY,
      value: true,
      updatedAt: Date.now(),
    });
  } catch {
    /* best-effort — the localStorage hint already gates the next load */
  }
}
